import React, { useState, useCallback, useEffect } from 'react';
import { ShowAlert } from '../../GlobalComponent/Alerts/Alert';
import {
  fetchChiJobs,
  fetchChiJobStatus,
  downloadChiJob,
  ChiJob,
  ChiJobStatus,
  JobListMeta,
} from './ChiDataTrackingFun';
import './ChiDataTracking.css';

function statusBadgeClass(status: string) {
  const map: Record<string, string> = {
    COMPLETED: 'chi-badge--completed',
    PROCESSING: 'chi-badge--processing',
    FAILED: 'chi-badge--failed',
    PENDING: 'chi-badge--pending',
  };
  return map[status] ?? 'chi-badge--default';
}

function progressFillClass(pct: number, failed: number, total: number) {
  if (pct < 100) return '';
  if (failed === total && total > 0) return 'chi-progress-bar-fill--failed';
  if (failed > 0) return 'chi-progress-bar-fill--partial';
  return 'chi-progress-bar-fill--complete';
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

// ── component ─────────────────────────────────────────────────────────────────

const ChiDataTracking: React.FC = () => {
  const [jobs, setJobs] = useState<ChiJob[]>([]);
  const [meta, setMeta] = useState<JobListMeta>({ total: 0, page: 1, limit: 10, totalPages: 1 });
  const [loadingJobs, setLoadingJobs] = useState(false);
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);

  const [jobStatuses, setJobStatuses] = useState<Record<string, ChiJobStatus>>({});
  const [checking, setChecking] = useState<Record<string, boolean>>({});
  const [downloading, setDownloading] = useState<Record<string, boolean>>({});

  // ── fetch ────────────────────────────────────────────────────────────────
  const loadJobs = useCallback(async (pageNo: number, limit: number) => {
    setLoadingJobs(true);
    try {
      const { jobs: list, meta: m } = await fetchChiJobs(pageNo, limit);
      setJobs(list);
      setMeta(m);
    } catch (e: any) {
      ShowAlert('warning', e?.response?.data?.message || 'Failed to fetch CHI jobs');
    } finally {
      setLoadingJobs(false);
    }
  }, []);

  useEffect(() => {
    loadJobs(page, pageLimit);
  }, [page, pageLimit, loadJobs]);

  // ── per-job handlers ─────────────────────────────────────────────────────
  const handleCheckStatus = useCallback(async (job: ChiJob) => {
    setChecking(prev => ({ ...prev, [job.jobId]: true }));
    try {
      const status = await fetchChiJobStatus(job.jobId);
      setJobStatuses(prev => ({ ...prev, [job.jobId]: status }));
    } catch (e: any) {
      ShowAlert('warning', e?.response?.data?.message || 'Failed to fetch job status');
    } finally {
      setChecking(prev => ({ ...prev, [job.jobId]: false }));
    }
  }, []);

  const handleDownload = useCallback(async (job: ChiJob) => {
    setDownloading(prev => ({ ...prev, [job.jobId]: true }));
    try {
      await downloadChiJob(job.jobId);
      ShowAlert('success', 'Download started');
    } catch (e: any) {
      ShowAlert('warning', e?.response?.data?.message || 'Download failed');
    } finally {
      setDownloading(prev => ({ ...prev, [job.jobId]: false }));
    }
  }, []);

  // ── render ───────────────────────────────────────────────────────────────
  return (
    <div className="chi-tracking-container">

      {/* ── Refresh bar ── */}
      <div className="chi-refresh-bar">
        <span className="chi-page-heading">CHI Data Upload Tracking</span>
        <button
          className={`chi-refresh-btn${loadingJobs ? ' spinning' : ''}`}
          onClick={() => loadJobs(page, pageLimit)}
          disabled={loadingJobs}
        >
          <span className="chi-refresh-icon">↻</span>
          {loadingJobs ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* ── Table card ── */}
      <div className="chi-table-card">
        {loadingJobs && jobs.length === 0 ? (
          <div className="chi-state-box">
            <div className="chi-spinner" />
            <span className="chi-state-text">Loading CHI jobs…</span>
          </div>
        ) : jobs.length === 0 ? (
          <div className="chi-state-box">
            <span className="chi-state-icon">📭</span>
            <span className="chi-state-text">No CHI jobs found.</span>
          </div>
        ) : (
          <table className="chi-table">
            <thead>
              <tr>
                <th>Job ID</th>
                <th>Created At</th>
                <th>Status</th>
                <th>Progress</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map(job => {
                const s = jobStatuses[job.jobId];
                const status = s?.status ?? job.status;
                const pct = s?.percentage ?? job.percentage;
                const completed = s?.completed ?? job.completed;
                const failed = s?.failed ?? job.failed;
                const total = s?.total ?? job.total;
                const isReady = s?.canDownload ?? job.canDownload;

                return (
                  <tr key={job.jobId}>
                    <td>
                      <span className="chi-job-id" title={job.jobId}>{job.jobId}</span>
                    </td>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 14, color: '#4a5568', fontWeight: 500 }}>
                      {formatDate(job.createdAt)}
                    </td>
                    <td>
                      <span className={`chi-badge ${statusBadgeClass(status)}`}>{status}</span>
                    </td>
                    <td className="chi-progress-cell">
                      <div className="chi-progress-meta">
                        <span className="chi-progress-counts">
                          {completed.toLocaleString()} / {total.toLocaleString()}
                          {failed > 0 && <span className="failed-count"> · {failed} failed</span>}
                        </span>
                        <span className="chi-progress-pct">{pct}%</span>
                      </div>
                      <div className="chi-progress-bar-track">
                        <div
                          className={`chi-progress-bar-fill ${progressFillClass(pct, failed, total)}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </td>
                    <td>
                      <div className="chi-action-group">
                        <button
                          className="chi-action-btn chi-action-btn--check"
                          onClick={() => handleCheckStatus(job)}
                          disabled={checking[job.jobId]}
                        >
                          <>↺ Refresh</>
                        </button>
                        <button
                          className="chi-action-btn chi-action-btn--download"
                          onClick={() => handleDownload(job)}
                          disabled={!isReady || downloading[job.jobId]}
                        >
                          {downloading[job.jobId]
                            ? <><span className="chi-btn-spinner" /> Downloading</>
                            : <>⬇ Download</>}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* ── Pagination ── */}
      <div className="chi-pagination">
        <span className="chi-pagination-info">
          {meta.total > 0
            ? `Showing ${(page - 1) * pageLimit + 1}–${Math.min(page * pageLimit, meta.total)} of ${meta.total} jobs`
            : 'No jobs'}
        </span>

        <div className="chi-pagination-controls">
          <div className="chi-limit-group">
            <span className="chi-limit-label">Rows per page:</span>
            <select
              className="chi-limit-select"
              value={pageLimit}
              onChange={e => {
                setPageLimit(Number(e.target.value));
                setPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <button className="chi-page-btn" onClick={() => setPage(p => p - 1)} disabled={page === 1 || loadingJobs}>
            ← Prev
          </button>
          <span className="chi-page-label">Page {page} of {meta.totalPages || 1}</span>
          <button className="chi-page-btn" onClick={() => setPage(p => p + 1)} disabled={page === (meta.totalPages || 1) || loadingJobs}>
            Next →
          </button>
        </div>
      </div>

    </div>
  );
};

export default ChiDataTracking;
